import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as FileSaver from 'file-saver';

import { WelcomeCredentials } from '../../services/welcome-modal.service';

type DomToImageModule = typeof import('dom-to-image-more');

// One-time post-registration welcome — ADAPTIVE:
//   - credentials mode (one-click): shows the generated username/password
//     with save-to-file / save-to-picture actions (this modal replaced the
//     old credentials screen inside the login popup, so it is the ONLY place
//     the generated password is ever displayed).
//   - plain mode (normal registration): welcome message only (the user chose
//     their own credentials).
// Two exits either way:
//   'deposit' → the opener (main-layout) launches the unified deposit picker
//   undefined → just closes
@Component({
  selector: 'app-welcome-popup',
  templateUrl: './welcome-popup.component.html',
  styleUrls: ['./welcome-popup.component.scss'],
})
export class WelcomePopupComponent {
  private readonly isBrowser: boolean;
  private domToImage?: DomToImageModule;

  credentials: WelcomeCredentials | null = null;
  showPassword = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.credentials =
      (this.config.data && this.config.data.credentials) || null;
  }

  get passwordDisplay(): string {
    if (!this.credentials) {
      return '';
    }
    return this.showPassword ? this.credentials.password : '••••••••••';
  }

  saveToFile(): void {
    if (!this.credentials) {
      return;
    }
    const data = `Username: ${this.credentials.username}\nPassword: ${this.credentials.password}`;
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, 'user_credentials.txt');
  }

  async saveToImage(): Promise<void> {
    if (!this.isBrowser || !this.credentials) {
      return;
    }
    const element = document.getElementById('welcomeCredsImage');
    if (!element) {
      return;
    }
    const domtoimage = await this.getDomToImage();
    domtoimage
      .toJpeg(element, {
        quality: 0.95,
        bgcolor: '#000000',
        width: element.offsetWidth,
        height: element.offsetHeight,
      })
      .then((dataUrl: string) => {
        const link = document.createElement('a');
        link.download = `user-credentials-${this.credentials.username}.jpeg`;
        link.href = dataUrl;
        link.click();
      })
      .catch(() => {
        // Image export failing must not break the flow — the save-to-file
        // path remains available.
      });
  }

  private async getDomToImage(): Promise<DomToImageModule> {
    if (!this.domToImage) {
      const module = await import('dom-to-image-more');
      this.domToImage = module.default ?? module;
    }
    return this.domToImage;
  }

  depositNow(): void {
    this.ref.close('deposit');
  }

  closeModal(): void {
    this.ref.close();
  }
}
